import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Button from 'react-bootstrap/Button';
import { Spinner } from 'react-bootstrap';
import PropdocsModal from '../modal/PropdocsModal';
import { TitleFormattedMessage } from '../titleFormattedMessage';

interface CreateAbstractModalProps {
  show: boolean;
  onHide: () => void;
  onConfirm: (format: 'short' | 'long') => void;
  isCreating: boolean;
  documentTitle: string;
}

export const CreateAbstractModal: React.FC<CreateAbstractModalProps> = ({
  show,
  onHide,
  onConfirm,
  isCreating,
  documentTitle,
}) => {
  const [selectedFormat, setSelectedFormat] = useState<'short' | 'long'>('short');

  const handleConfirm = () => {
    onConfirm(selectedFormat);
  };

  return (
    <PropdocsModal
      show={show}
      setShow={onHide}
      size="lg"
      title={<FormattedMessage id="create-abstract.title" defaultMessage="Create Abstract" />}
      disableClose={isCreating}
    >
      <div>
        <p style={{ marginBottom: '1.5rem' }}>
          <FormattedMessage
            id="create-abstract.description"
            defaultMessage="Create an abstract of: {documentTitle}"
            values={{ documentTitle: <strong>{documentTitle}</strong> }}
          />
        </p>

        {/* Format Selection */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h5 style={{ marginBottom: '1rem' }}>
            <FormattedMessage id="create-abstract.format-label" defaultMessage="Abstract Format" />
          </h5>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* Short Form Option */}
            <label
              style={{
                padding: '1rem',
                border: `2px solid ${selectedFormat === 'short' ? '#006c90' : '#ddd'}`,
                borderRadius: '8px',
                cursor: 'pointer',
                backgroundColor: selectedFormat === 'short' ? '#f0f8ff' : 'transparent',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <input
                type="radio"
                name="format"
                value="short"
                checked={selectedFormat === 'short'}
                onChange={() => setSelectedFormat('short')}
                style={{ marginRight: '0.75rem', flexShrink: 0 }}
                disabled={isCreating}
              />
              <div>
                <strong>Short Form</strong> - Concise summary focusing on deal points only
              </div>
            </label>

            {/* Long Form Option */}
            <label
              style={{
                padding: '1rem',
                border: `2px solid ${selectedFormat === 'long' ? '#006c90' : '#ddd'}`,
                borderRadius: '8px',
                cursor: 'pointer',
                backgroundColor: selectedFormat === 'long' ? '#f0f8ff' : 'transparent',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <input
                type="radio"
                name="format"
                value="long"
                checked={selectedFormat === 'long'}
                onChange={() => setSelectedFormat('long')}
                style={{ marginRight: '0.75rem', flexShrink: 0 }}
                disabled={isCreating}
              />
              <div>
                <strong>Long Form</strong> - Comprehensive detailed analysis with all terms, conditions and obligations
              </div>
            </label>
          </div>
        </div>

        <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1.5rem' }}>
          <FormattedMessage
            id="create-abstract.timing"
            defaultMessage="The abstract will be created as a new document in this deal. Generation may take a few minutes."
          />
        </p>

        {/* Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '1rem' }}>
          <Button variant="link" className="text-secondary" onClick={onHide} disabled={isCreating}>
            <TitleFormattedMessage id="cta.cancel" defaultMessage="Cancel" />
          </Button>
          <Button variant="primary" onClick={handleConfirm} disabled={isCreating}>
            {isCreating && <Spinner animation="border" size="sm" style={{ marginRight: '0.5rem' }} />}
            <TitleFormattedMessage
              id="create-abstract.confirm"
              defaultMessage={isCreating ? 'Creating...' : 'Create Abstract'}
            />
          </Button>
        </div>
      </div>
    </PropdocsModal>
  );
};
